import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MdContentCopy, MdSearch } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function JsonBlock({ value }) {
  if (value == null) return <Text color="gray.400" fontSize="sm">—</Text>;
  let pretty;
  try {
    pretty = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  } catch {
    pretty = String(value);
  }
  if (!pretty || pretty === "{}" || pretty === "[]") {
    return <Text color="gray.400" fontSize="sm">—</Text>;
  }
  return (
    <Box
      as="pre"
      fontSize="xs"
      fontFamily="mono"
      bg="gray.50"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={2}
      maxH="320px"
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      {pretty}
    </Box>
  );
}

function KV({ label, children }) {
  return (
    <Flex gap={2} fontSize="sm" align="baseline" wrap="wrap">
      <Text color="gray.500" minW="160px" flexShrink={0}>{label}</Text>
      <Box flex="1" minW={0}>
        {children == null || children === "" ? <Text color="gray.400">—</Text> : children}
      </Box>
    </Flex>
  );
}

function CopyableId({ value }) {
  if (!value) return <Text color="gray.400">—</Text>;
  return (
    <Flex align="center" gap={1}>
      <Code fontSize="xs">{value}</Code>
      <Tooltip label="Copy">
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Copy"
          icon={<MdContentCopy size={12} />}
          onClick={() => navigator.clipboard?.writeText(value)}
        />
      </Tooltip>
    </Flex>
  );
}

export default function InsightoAdminCoupons() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const location = useLocation();

  const initialCode = new URLSearchParams(location.search).get("code") || "";
  const [inputCode, setInputCode] = useState(initialCode);
  const [status, setStatus] = useState(initialCode ? "loading" : "idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null);

  async function fetchCoupon(code) {
    const c = (code || "").trim();
    if (!c) return;
    setStatus("loading");
    setErrorMsg("");
    setData(null);
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/insighto-admin/coupons/${encodeURIComponent(c)}`,
        "GET"
      );
      if (!res?.data) {
        throw new Error(res?.message || "No data returned");
      }
      setData(res.data);
      setStatus("success");
      navigate(`/insighto-admin/coupons?code=${encodeURIComponent(c)}`, { replace: true });
    } catch (e) {
      setStatus("error");
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message;
      setErrorMsg(detail || "Failed to look up coupon");
    }
  }

  function handleLookup() {
    fetchCoupon(inputCode);
  }

  useEffect(() => {
    if (initialCode) fetchCoupon(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const coupon = data?.coupon;
  const user = data?.user;

  return (
    <Box p={6}>
      <Heading size="lg" mb={4}>Coupon Lookup</Heading>

      <Flex gap={2} mb={6}>
        <Input
          placeholder="Enter coupon code"
          value={inputCode}
          onChange={(e) => setInputCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleLookup();
            }
          }}
          fontFamily="mono"
          autoFocus
        />
        <Button
          type="button"
          colorScheme="blue"
          leftIcon={<MdSearch />}
          isLoading={status === "loading"}
          isDisabled={!inputCode.trim()}
          onClick={handleLookup}
        >
          Look up
        </Button>
      </Flex>

      {status === "error" && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {errorMsg}
        </Alert>
      )}

      {status === "loading" && (
        <Flex justify="center" py={8}><Spinner /></Flex>
      )}

      {status === "success" && coupon && (
        <VStack align="stretch" spacing={4}>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
              <Heading size="sm">Coupon</Heading>
              <Badge
                colorScheme={coupon.is_used ? "red" : "green"}
                fontSize="0.85em"
                px={2}
                py={1}
              >
                {coupon.is_used ? "Redeemed" : "Available"}
              </Badge>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
              <KV label="Code"><Code>{coupon.coupon_code}</Code></KV>
              <KV label="Coupon ID"><CopyableId value={coupon.id} /></KV>
              <KV label="Provider">{coupon.coupon_provider || "—"}</KV>
              <KV label="Type">{coupon.coupon_type || "—"}</KV>
              <KV label="Billing plan group">{coupon.billing_plan_group || "—"}</KV>
              <KV label="Redeemed on">{coupon.is_used ? fmtDate(coupon.used_on) : "—"}</KV>
              <KV label="Created">{fmtDate(coupon.created_at)}</KV>
              <KV label="Updated">{fmtDate(coupon.updated_at)}</KV>
            </SimpleGrid>
            {coupon.attributes && Object.keys(coupon.attributes).length > 0 && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" color="gray.500" mb={1}>attributes</Text>
                <JsonBlock value={coupon.attributes} />
              </>
            )}
          </Box>

          {coupon.is_used && (
            <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
              <Heading size="sm" mb={3}>Linked Account</Heading>
              {!user ? (
                <Alert status="warning" fontSize="sm">
                  <AlertIcon />
                  Coupon is marked redeemed but the linked user was not found
                  (account may have been deleted).
                </Alert>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={2} spacingX={6}>
                  <KV label="Email">
                    {user.email ? <Code>{user.email}</Code> : "—"}
                  </KV>
                  <KV label="User ID"><CopyableId value={user.id} /></KV>
                  <KV label="First name">{user.first_name || "—"}</KV>
                  <KV label="Last name">{user.last_name || "—"}</KV>
                  <KV label="Active">
                    <Badge colorScheme={user.is_active ? "green" : "gray"}>
                      {user.is_active ? "yes" : "no"}
                    </Badge>
                  </KV>
                  <KV label="Current billing plan">
                    {user.billing_plan_name || "—"}
                  </KV>
                  <KV label="Account created">{fmtDate(user.created_at)}</KV>
                </SimpleGrid>
              )}
            </Box>
          )}

          {!coupon.is_used && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              This coupon has not been redeemed yet.
            </Alert>
          )}
        </VStack>
      )}
    </Box>
  );
}
